import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Apple } from "lucide-react";
import { BRAND } from "../../data/content";

const LINKS = [
  { label: "Scanners", href: "#scanners" },
  { label: "Why WhatThe?", href: "#why" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (!el) return;
    requestAnimationFrame(() => {
      if (window.lenis) {
        window.lenis.scrollTo(el, { offset: -80 });
      } else {
        el.scrollIntoView({ behavior: "smooth" });
      }
    });
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-page/85 backdrop-blur-xl border-b border-line py-2.5"
          : "bg-transparent border-b border-transparent py-4"
      }`}
    >
      <div className="mx-auto max-w-[1240px] px-5 md:px-8 flex items-center justify-between gap-4">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5"
        >
          <img src={BRAND.logo} alt="WhatThe?" className="h-9 w-9 rounded-xl" />
          <span className="text-lg font-bold tracking-tight text-ink">
            WhatThe<span className="text-food-accent">?</span> Scan
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              onClick={() => go(l.href)}
              className="relative text-sm font-medium text-slate hover:text-ink transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <a
          href={BRAND.appStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-download"
          className="hidden md:inline-flex items-center gap-2 rounded-full bg-ink text-white px-5 py-2.5 text-sm font-semibold transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#1e293b]"
        >
          <Apple className="h-4 w-4" strokeWidth={1.8} />
          Download
        </a>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden flex flex-col gap-1.5"
          aria-label="Menu"
        >
          <span className={`h-0.5 w-6 rounded bg-ink transition-transform ${open ? "translate-y-[8px] rotate-45" : ""}`} />
          <span className={`h-0.5 w-6 rounded bg-ink transition-opacity ${open ? "opacity-0" : ""}`} />
          <span className={`h-0.5 w-6 rounded bg-ink transition-transform ${open ? "-translate-y-[8px] -rotate-45" : ""}`} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-page/97 backdrop-blur-xl border-t border-line"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  onClick={() => go(l.href)}
                  className="text-left text-xl font-semibold text-ink"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={BRAND.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-download"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-ink text-white px-5 py-3 text-base font-semibold"
              >
                <Apple className="h-5 w-5" strokeWidth={1.8} />
                Download on the App Store
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
